/**
 * The marketing nav link shape, shared by the header row (MarketingLayout) and
 * its below-md stand-in (MarketingMobileNav).
 *
 * One definition on purpose. The two components render the SAME list, so a type
 * per file is a drift waiting to happen — and it lives here rather than in
 * either component because a non-component export from a component file breaks
 * react-refresh (`only-export-components`).
 *
 * An entry is EITHER a route or a landing-section anchor: never both, never
 * neither. The shape this replaces had `href` required and `to` optional, which
 * could express "a route that also carries a stale anchor" — and did, for
 * Features, whose `href: "#features"` stopped navigating anywhere the moment it
 * gained a `to` and then outlived the section itself (BEN-141 items 2 and 9).
 * That state was invisible to the type checker. Now it cannot be written down.
 */
export type MarketingNavLink = { label: string; show: string } & (
  | { kind: "route"; to: string }
  | { kind: "section"; hash: string }
  // Doc 3 B2/B3 (Anthony's package, 2026-10-28): "How it works" opens the walkthrough
  // in place; it navigates nowhere, so it's an action, not a route or an anchor.
  | { kind: "action"; action: "how-it-works" }
);

/**
 * Stable list key, and the thing the entry actually navigates to — the two were
 * the same field before, which is why the dead `href` survived: it was still
 * load-bearing as a React key.
 */
export const navKey = (link: MarketingNavLink) =>
  link.kind === "route" ? link.to : link.kind === "section" ? link.hash : link.action;

/** What "Back to [page]" calls the marketing page the walkthrough opened over (doc 3 E6;
 *  over the splash page it's "the home page", Part 4 #5). */
export function marketingPageName(pathname: string, splashName: string): string {
  if (pathname === "/features") return "Features";
  if (pathname === "/contact") return "Contact";
  return splashName;
}

/** "See how it works · 1 min" must never break between "1" and "min" (a non-breaking
 *  space at render; the shared copy keeps its plain space). */
export function keepUnitTogether(copy: string): string {
  return copy.replace(/(\d) (min)\b/g, "$1\u00a0$2");
}
